<template>
<main>
  <div class="mx-auto max-w-5xl space-y-4">
    <section v-if="hasRecordId" class="rounded-2xl border border-slate-200 bg-white px-3 pt-3 pb-2 shadow-sm sm:px-4">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0 flex-1 space-y-2 overflow-hidden sm:pr-3">
          <div>
            <p v-if="formState.event && formState.event.title" class="truncate text-sm font-medium text-slate-600 sm:text-base">{{ formState.event.title }}</p>
            <h2 class="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">Ticket list</h2>
          </div>
        </div>

        <div class="w-full self-auto rounded-xl bg-slate-50 sm:ml-3 sm:w-auto sm:flex-shrink-0 sm:self-start sm:min-w-[240px]">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Scanned in</p>
          </div>
          <div class="flex items-center justify-between gap-4">
            <div>
              <p class="mt-1 text-2xl font-semibold text-slate-900">{{ totalScanned }}</p>
            </div>
            <div class="text-right text-xs leading-2 text-slate-500">
              <div>Adults: {{ stats.adult }}</div>
              <div>Kids: {{ stats.child }}</div>
            </div>
          </div>
        </div>
      </div>

      <form @submit.prevent="searchRegistrations" class="mt-2 flex flex-col gap-2 sm:flex-row">
        <div class="flex flex-1 items-stretch gap-2">
          <label class="block flex-1">
            <span class="sr-only">Search registrations</span>
            <input
              ref="searchInput"
              v-model="filterText"
              type="text"
              placeholder="Search by delegate name or email"
              class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-200 focus:bg-white"
              @keyup.enter="searchRegistrations"
              aria-label="Search registrations"
              :disabled="formState.searching"
            />
          </label>
          <button
            type="submit"
            class="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            aria-label="Search"
            title="Search"
            :disabled="formState.searching"
          >
            <MagnifyingGlassIcon class="h-5 w-5" />
          </button>
          <button
            type="button"
            class="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            @click="clearFilter"
            aria-label="Clear search"
            title="Clear search"
            :disabled="formState.searching || !filterText"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
      </form>

      <div class="ml-2">
        <button
          type="button"
          class="text-xs font-medium text-slate-500 decoration-slate-300 underline-offset-4 transition hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
          @click="listAllRegistrations"
          :disabled="formState.searching"
        >
          List all
        </button>
      </div>
    </section>

    <section v-else class="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-4">
      <div class="space-y-1">
        <p class="text-sm font-medium text-slate-600 sm:text-base">Choose event</p>
        <h2 class="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">Registration ticket lists</h2>
      </div>
    </section>

    <ErrorMessage :formError="formState.loadError"></ErrorMessage>

    <section
      v-if="!hasRecordId && formState.loadingEvents"
      class="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center shadow-sm"
    >
      <h3 class="text-lg font-semibold text-slate-900">Loading events</h3>
      <p class="mt-2 text-sm text-slate-500">Please wait while available registration lists are fetched.</p>
    </section>

    <section
      v-else-if="!hasRecordId && formState.eventRecords && formState.eventRecords.length > 0"
      class="space-y-3"
    >
      <div class="flex items-center justify-between px-1">
        <p class="text-sm text-slate-500">
          {{ formState.eventRecords.length }}
          {{ formState.eventRecords.length === 1 ? 'event' : 'events' }}
        </p>
      </div>

      <div class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div
          v-for="item in formState.eventRecords"
          :key="item.id"
          class="border-b border-slate-200 last:border-b-0"
        >
          <button
            type="button"
            @click="openEventRegistration(item)"
            class="group w-full px-3 py-3 text-left transition hover:bg-gray-100 sm:px-4"
          >
            <h3 class="text-sm font-semibold text-slate-900 sm:text-base">
              {{ item.title || 'Untitled event' }}
            </h3>
            <p class="mt-1 truncate text-xs text-slate-500">
              {{ item.registration }}
            </p>
          </button>
        </div>
      </div>
    </section>

    <section
      v-else-if="!hasRecordId && formState.eventRecords && formState.eventRecords.length === 0"
      class="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center shadow-sm"
    >
      <h3 class="text-lg font-semibold text-slate-900">No registration lists found</h3>
      <p class="mt-2 text-sm text-slate-500">No event records with registration links were returned.</p>
    </section>

    <section v-if="hasRecordId && formState.filteredRegistrations && formState.filteredRegistrations.length > 0" class="space-y-3">
      <div class="flex items-center justify-between px-1">
        <p class="text-sm text-slate-500">
          {{ formState.filteredRegistrations.length }}
          {{ formState.filteredRegistrations.length === 1 ? 'result' : 'results' }}
        </p>
      </div>

      <div class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div
          v-for="item in formState.filteredRegistrations"
          :key="item.guid"
          class="border-b border-slate-200 last:border-b-0 hover:bg-gray-100"
        >
          <button
            type="button"
            @click="selectRegistration(item.guid)"
            class="group w-full px-3 py-3 text-left transition sm:px-4"
          >
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between">
              <div class="min-w-0 flex-1">
                <div class="flex justify-between gap-2">
                  <h3 class="truncate text-sm font-semibold text-slate-900 sm:text-base">
                    {{ item.firstName }} {{ item.lastName }}
                  </h3>
                  <div class="flex items-center justify-start gap-2 text-slate-500 sm:justify-end">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100" :title="item.child ? 'Child delegate' : 'Adult delegate'">
                      <UserIcon class="h-4 w-4" :class="item.child ? 'text-amber-600' : 'text-sky-600'" />
                    </span>
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100" :title="item.paid ? 'Paid' : 'Unpaid'">
                      <CheckCircleIcon v-if="item.paid" class="h-4 w-4 text-emerald-600" />
                      <XMarkIcon v-else class="h-4 w-4 text-rose-500" />
                    </span>
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100" :title="item.registered ? 'Registered' : 'Not registered'">
                      <CheckCircleIcon v-if="item.registered" class="h-4 w-4 text-emerald-600" />
                      <XMarkIcon v-else class="h-4 w-4 text-slate-400" />
                    </span>
                  </div>
                </div>

                <div class="mt-1 text-xs text-slate-500">
                  {{ item.ticketTitle }}
                  <span v-if="item.cancelledAt || item.cancelled" class="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                    CANCELLED
                  </span>
                </div>

                <div class="mt-1 space-y-1 text-xs text-slate-500">
                  <p v-if="item.child || item.age || item.gender">
                    {{ formatAgeGender(item) }}
                    <span v-if="item.colour || item.colourCode" class="mx-2 text-slate-300">|</span>
                    <span v-if="item.colour || item.colourCode">
                      {{ item.colour || 'Band' }}<span v-if="item.colourCode">-{{ String(item.colourCode).padStart(3, '0') }}</span>
                    </span>
                  </p>
                  <p v-else-if="item.colour || item.colourCode">
                    {{ item.colour || 'Band' }}<span v-if="item.colourCode">-{{ String(item.colourCode).padStart(3, '0') }}</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="mt-1 pt-2 border-t border-slate-300 text-xs text-slate-500 py-1">
              <span class="font-semibold uppercase tracking-wide text-slate-400">Order:</span>
              <span class="ml-1 font-mono text-slate-700">#{{ item.checkoutId || '-' }}</span>
              <span class="mx-2 text-slate-300">|</span>
              <span class="break-all">{{ item.email || '-' }}</span>
              <span v-if="item.checkoutFirstName || item.checkoutLastName" class="mx-2 text-slate-300">|</span>
              <span>{{ item.checkoutFirstName }} {{ item.checkoutLastName }}</span>
            </div>
          </button>
        </div>
      </div>
    </section>

    <section
      v-else-if="hasRecordId && formState.registrations === undefined && !formState.loadError"
      class="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center shadow-sm"
    >
      <h3 class="text-lg font-semibold text-slate-900">Start with a search</h3>
      <p class="mt-2 text-sm text-slate-500">Enter a delegate name or email address to load matching registrations.</p>
    </section>

    <section
      v-else-if="hasRecordId && !formState.searching && formState.registrations && formState.filteredRegistrations && formState.filteredRegistrations.length === 0"
      class="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center shadow-sm"
    >
      <h3 class="text-lg font-semibold text-slate-900">No registrations found</h3>
      <p class="mt-2 text-sm text-slate-500">Try a different name, email, or clear the current search.</p>
    </section>
  </div>
</main>
</template>

<script>
import axios from 'axios'
import { defineComponent, ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import responseHelper from '@/helpers/response.helper'
import ErrorMessage from '@/components/ErrorMessage.vue'
import { XMarkIcon, CheckCircleIcon, UserIcon, MagnifyingGlassIcon } from '@heroicons/vue/20/solid'

export default defineComponent({
  components: {
    ErrorMessage,
    XMarkIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon,
    UserIcon
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const recordId = ref(route.params.recordId)
    const filterText = ref('')
    const searchInput = ref(null)
    const hasRecordId = computed(() => !!recordId.value)

    const popUpState = reactive({
      open: false
    })

    const formState = reactive({
      selectedRegistration: undefined,
      registrations: undefined,
      filteredRegistrations: undefined,
      event: undefined,
      loadError: responseHelper.noError,
      formSubmitted: false,
      formError: responseHelper.noError,
      successMessage: '',
      searching: false,
      loadingEvents: false,
      eventRecords: undefined
    })

    // Stats state
    const stats = reactive({
      adult: 0,
      child: 0
    })

    const totalScanned = computed(() => {
      return stats.adult + stats.child
    })

    let statsInterval = null

    // Periodically fetch stats from remote endpoint
    const fetchStats = async () => {
      if (!hasRecordId.value) return
      try {
        const response = await axios.post(
          '/pub/registrations/stats',
          { recordId: recordId.value }
        )
        if (response?.data?.success && response?.data?.result) {
          stats.adult = response.data.result.adult || 0
          stats.child = response.data.result.child || 0
        }
      } catch (e) {
        // Optionally handle error
      }
    }

    const fetchEventRecords = async () => {
      formState.loadError = responseHelper.noError
      formState.loadingEvents = true
      formState.eventRecords = undefined
      try {
        const response = await axios.get('https://prayerstorm.pockethost.io/api/collections/events/records', {
          params: {
            page: 1,
            perPage: 100,
            sort: '-created'
          }
        })

        if (response?.data?.items && Array.isArray(response.data.items)) {
          formState.eventRecords = response.data.items.filter(item => item.registration)
        } else {
          formState.loadError = responseHelper.unexpectedResult
          formState.eventRecords = []
        }
      } catch (error) {
        formState.loadError = responseHelper.getMessageV2(error)
        formState.eventRecords = []
      } finally {
        formState.loadingEvents = false
      }
    }

    onMounted(() => {
      if (!hasRecordId.value) {
        fetchEventRecords()
        return
      }

      fetchStats()
      statsInterval = setInterval(fetchStats, 60000) // every 60 seconds
    })

    onUnmounted(() => {
      if (statsInterval) clearInterval(statsInterval)
    })

    // Don't auto-load registrations on mount

    const fetchRegistrations = async (search = '') => {
      formState.loadError = responseHelper.noError
      formState.registrations = undefined
      formState.filteredRegistrations = undefined
      formState.searching = true
      try {
        const response = await axios.post(
          'pub/registrations/list',
          {
            recordId: recordId.value,
            search: search // send search term to backend
          }
        )
        if (response?.data?.success && response?.data?.result) {
          formState.registrations = response.data.result.registrations
          formState.filteredRegistrations = response.data.result.registrations
          formState.event = response.data.result.event
        } else {
          formState.loadError = responseHelper.unexpectedResult
        }
      } catch (error) {
        formState.loadError = responseHelper.getMessageV2(error)
      } finally {
        formState.searching = false
      }
    }

    const searchRegistrations = async () => {
      if (formState.searching) return
      if (!filterText.value.trim()) {
        formState.registrations = undefined
        formState.filteredRegistrations = undefined
        formState.event = undefined
        return
      }
      await fetchRegistrations(filterText.value)
    }

    const listAllRegistrations = async () => {
      if (formState.searching) return
      filterText.value = ''
      await fetchRegistrations('')
    }

    const clearFilter = async () => {
      if (formState.searching) return
      filterText.value = ''
      formState.registrations = undefined
      formState.filteredRegistrations = undefined
      formState.event = undefined
      if (searchInput.value) {
        searchInput.value.focus()
      }
    }

    const formatAgeGender = (item) => {
      if (!item.child) return 'Adult'

      const parts = []

      if (item.age) {
        parts.push('Age ' + item.age)
      }

      if (item.gender) {
        parts.push(item.gender === 'F' ? 'Girl' : item.gender === 'M' ? 'Boy' : item.gender)
      }

      if (parts.length === 0) {
        return 'Child'
      }

      return parts.join(' / ')
    }

    const selectRegistration = async (guid) => {
      router.push('/registration-ticket/verify/' + guid)
    }

    const openEventRegistration = (item) => {
      if (!item || !item.registration) return

      try {
        const parsed = new URL(item.registration)
        if (parsed.origin === window.location.origin) {
          router.push(parsed.pathname)
          return
        }
      } catch (e) {
        // If registration is a relative path, push directly.
      }

      if (item.registration.startsWith('/')) {
        router.push(item.registration)
      } else {
        window.location.href = item.registration
      }
    }

    return {
      formState,
      hasRecordId,
      searchRegistrations,
      listAllRegistrations,
      filterText,
      searchInput,
      clearFilter,
      formatAgeGender,
      popUpState,
      selectRegistration,
      openEventRegistration,
      stats,
      totalScanned
    }
  }
})
</script>
